import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Forward, SearchX, UserRoundSearch } from 'lucide-react'

import { UserDisplay } from '@/components'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import {
  useDeliverableSetsStore,
  validateTargetDateIsOverdue,
} from '@/features/deliverable-set'
import { ReviewerList } from '@/features/deliverable-set/components/ReviewerList'
import { DELIVERABLE_SET_STATUSES } from '@/features/deliverable-set/constants'
import { isUserReviewer } from '@/features/view-tfl-uploaded/utils/validation'
import { useCurrentUser } from '@/hooks/useAuth'
import { DeliverableSetStatus, ResponseDeliverableSet, User } from '@/schema'
import { cn, formatDisplayDate } from '@/utils'
import { DATE_PICKER_FORMAT } from '@/utils/constants'

import { getReviewerUsers, getViewerUsers } from '../utils'

type ApprovalInformationCardProps = {
  deliverableSet: ResponseDeliverableSet
  isOrgAdminOrProjectLead?: boolean
  isHaveFiles?: boolean
}

const LAST_SENT_FORMAT = 'MM/DD/YYYY - hh:mm:ss A'

type ApprovalAction = {
  show: boolean
  onClick?: () => void
  icon: React.ElementType
  labelKey: string
}

const getApprovalActions = (
  deliverableSet: ResponseDeliverableSet,
  setSendToReviewDeliverableSet: (item: ResponseDeliverableSet) => void,
  setCancelApprovalDeliverableSet: (item: ResponseDeliverableSet) => void,
  setDelegateDeliverableSet: (item: ResponseDeliverableSet) => void,
  currentUserId: string | undefined,
  isOrgAdminOrProjectLead = false,
  isHaveFiles = false
): ApprovalAction[] => [
  {
    show:
      deliverableSet.status === DeliverableSetStatus.DRAFT &&
      isOrgAdminOrProjectLead &&
      isHaveFiles,
    onClick: () => setSendToReviewDeliverableSet(deliverableSet),
    icon: UserRoundSearch,
    labelKey: 'deliverableSet.actions.sendApprovalRequest',
  },
  {
    show:
      deliverableSet.status === DeliverableSetStatus.IN_REVIEW &&
      isOrgAdminOrProjectLead &&
      isHaveFiles,
    onClick: () => setCancelApprovalDeliverableSet(deliverableSet),
    icon: SearchX,
    labelKey: 'deliverableSet.actions.cancelApprovalRequest',
  },
  {
    show:
      deliverableSet.status === DeliverableSetStatus.IN_REVIEW &&
      isUserReviewer(deliverableSet.reviewers, currentUserId),
    onClick: () => setDelegateDeliverableSet(deliverableSet),
    icon: Forward,
    labelKey: 'deliverableSet.actions.delegateReviewer',
  },
]

function getStatusTextClass(status?: DeliverableSetStatus) {
  switch (status) {
    case DeliverableSetStatus.IN_REVIEW:
      return 'text-warning-primary-600'
    case DeliverableSetStatus.APPROVED:
      return 'text-success-primary-600'
    case DeliverableSetStatus.DRAFT:
    case DeliverableSetStatus.ARCHIVED:
    default:
      return 'text-primary-900'
  }
}

export const ApprovalInformationBox: React.FC<ApprovalInformationCardProps> = ({
  deliverableSet,
  isOrgAdminOrProjectLead = false,
  // Check if the deliverable set has files
  isHaveFiles = false,
}) => {
  const { t } = useTranslation()
  const {
    setSendToReviewDeliverableSet,
    setCancelApprovalDeliverableSet,
    setDelegateDeliverableSet,
  } = useDeliverableSetsStore()
  const { data: currentUser } = useCurrentUser()

  const statusLabel = useMemo(
    () =>
      DELIVERABLE_SET_STATUSES[
        deliverableSet.status as keyof typeof DELIVERABLE_SET_STATUSES
      ] || '-',
    [deliverableSet.status]
  )

  const approvalActions = getApprovalActions(
    deliverableSet,
    setSendToReviewDeliverableSet,
    setCancelApprovalDeliverableSet,
    setDelegateDeliverableSet,
    currentUser?.id,
    isOrgAdminOrProjectLead,
    isHaveFiles
  )

  const dueDateText = useMemo(
    () =>
      deliverableSet.due_date
        ? formatDisplayDate(deliverableSet.due_date, DATE_PICKER_FORMAT)
        : '-',
    [deliverableSet.due_date]
  )

  const lastSentText = useMemo(
    () =>
      deliverableSet.last_approval_request_date
        ? formatDisplayDate(
            deliverableSet.last_approval_request_date,
            LAST_SENT_FORMAT
          )
        : '-',
    [deliverableSet.last_approval_request_date]
  )

  const fileReviewText = useMemo(
    () =>
      deliverableSet.snapshot_tfl_progress !== undefined &&
      deliverableSet.snapshot_tfl_progress !== null
        ? `${Number(deliverableSet.snapshot_tfl_progress).toFixed(0)}%`
        : '-',
    [deliverableSet.snapshot_tfl_progress]
  )

  const generateUserList = (users: User[], greyedOut: boolean) => {
    if (!users?.length) return <span>-</span>
    return <ReviewerList reviewers={users} greyedOut={greyedOut} />
  }

  const getReviewerList = () => {
    const { users, greyedOut } = getReviewerUsers(deliverableSet)
    return generateUserList(users, greyedOut)
  }

  const getViewerList = () => {
    const { users, greyedOut } = getViewerUsers(deliverableSet)
    return generateUserList(users, greyedOut)
  }

  return (
    <div className="bg-white border border-solid shadow-sm border-secondary rounded-xl">
      <div className="flex items-center justify-between p-6">
        <h2 className="m-0 text-lg font-semibold text-primary-900">
          {t('deliverableSet.approval.title')}
        </h2>
        <div className="flex gap-2">
          {approvalActions
            .filter((action) => action.show)
            .map((action) => {
              const IconComponent = action.icon
              return (
                <Button
                  key={action.labelKey}
                  variant="outlined"
                  onClick={action.onClick}
                  leadingIcon={
                    <IconComponent
                      size={16}
                      className={cn('text-foreground-fg-quaternary-400')}
                    />
                  }
                  className={cn('text-secondary-700 hover:bg-primary-hover')}
                >
                  {t(action.labelKey)}
                </Button>
              )
            })}
        </div>
      </div>

      <Separator />

      <div className="grid grid-cols-3 gap-x-10 gap-y-8 px-6 py-6 text-sm">
        {/* Status */}
        <div className="flex flex-col gap-1">
          <span className="text-secondary-700">
            {t('deliverableSet.approval.status')}
          </span>
          <span
            className={cn(
              'font-semibold',
              getStatusTextClass(deliverableSet.status)
            )}
          >
            {statusLabel}
          </span>
        </div>

        {/* Due date */}
        <div className="flex flex-col gap-1">
          <span className="text-secondary-700">
            {t('deliverableSet.approval.dueDate')}
          </span>
          <span
            className={cn(
              'font-semibold',
              validateTargetDateIsOverdue(deliverableSet?.due_date)
                ? 'text-error-600'
                : 'text-primary-900'
            )}
          >
            {dueDateText}
          </span>
        </div>

        {/* Owner */}
        <div className="flex flex-col gap-1">
          <span className="text-secondary-700">
            {t('deliverableSet.approval.owner')}
          </span>
          <span className="font-semibold text-primary-900">
            <UserDisplay user={deliverableSet.owner} showAvatar={false} />
          </span>
        </div>

        {/* Reviewer */}
        <div className="flex flex-col gap-1">
          <span className="text-secondary-700">
            {t('deliverableSet.approval.reviewer')}
          </span>
          <div className="font-semibold text-primary-900">
            {getReviewerList()}
          </div>
        </div>

        {/* Invited reviewers */}
        <div className="flex flex-col gap-1">
          <span className="text-secondary-700">
            {t('deliverableSet.approval.viewer')}
          </span>
          <div className="font-semibold text-primary-900">
            {getViewerList()}
          </div>
        </div>

        {/* File review */}
        <div className="flex flex-col gap-1">
          <span className="text-secondary-700">
            {t('deliverableSet.approval.fileReview')}
          </span>
          <span className="font-semibold text-primary-900">
            {fileReviewText}
          </span>
        </div>

        {/* Last Approval Request Sent */}
        <div className="flex flex-col gap-1">
          <span className="text-secondary-700">
            {t('deliverableSet.approval.lastApprovalRequestSent')}
          </span>
          <span className="font-semibold text-primary-900">{lastSentText}</span>
        </div>

        {/* Note */}
        <div className="flex flex-col gap-2 col-span-3">
          <span className="text-secondary-700">
            {t('deliverableSet.approval.note')}
          </span>
          <p className="m-0 font-semibold text-primary-900 whitespace-pre-wrap">
            {deliverableSet.last_approval_note || '-'}
          </p>
        </div>
      </div>
    </div>
  )
}
